import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useApolloClient } from '@apollo/client';
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import cn from 'classnames';
import { createPortal } from 'react-dom';

import { AuthContext } from 'auth';
import { EHostType } from 'types';

import { MapObjectType } from '../Components';
import { DraggableMapHost } from './components/DraggableMapHost';
import { DraggableMapObject } from './components/DraggableMapObject';
import { DraggableOverlay } from './components/DraggableOverlay';
import DraggableItem from './components/DraggbleItem';
import { Droppable } from './components/Droppable';
import { TextEditModal } from './components/TextEditModal';
import { GRID, SENSOR_CONFIG, DEFAULT_TEXT_STYLE } from './constants';
import { parseMapDragId } from './dragIds';
import {
  IHostInputFragment,
  IMapObject,
  IMapProps,
  ITextStyle,
} from './interaces';
import s from './Map.module.scss';
import { HOST_INPUT_FR } from './schema';
import { useSelectionHosts } from './UseSelectionHosts';
import { findBounds } from './utils';

export const Map = forwardRef<HTMLDivElement, IMapProps>(
  ({
    mapItems,
    onHostClick,
    selectedHostsIds,
    onContextMenu,
    onHostDoubleClick,
    openHostCreate,
    openBooking,
    canOpenTooltip,
    setClientUUID,
    isEdit,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    selectedItems,
    setSelectedItems,
    hostModalsState,
    selectedTool,
    mapObjects,
    onMapObjectsChange,
    setMapItems,
    isSave,
    isMinimal,
    zoomValue,
  }) => {
    const {
      data: { permissions },
    } = useContext(AuthContext);
    const apolloClient = useApolloClient();

    const mouseSensor = useSensor(MouseSensor, SENSOR_CONFIG);
    const sensors = useSensors(mouseSensor);
    const idCounter = useRef(1000);
    const getNextId = (): number => idCounter.current++;
    const [defaultRotate, setDefaultRotate] = useState(0);
    const [defaultTextStyle, setDefaultTextStyle] =
      useState(DEFAULT_TEXT_STYLE);
    const suppressMapClick = useRef(false);

    const {
      localRef,
      handleMouseDown,
      handleMouseMove,
      isSelecting,
      handleMouseUp,
      selectionStyle,
      handleClick,
    } = useSelectionHosts(isEdit, selectedItems, setSelectedItems);

    const handleRotateObject = (id: number): void => {
      onMapObjectsChange((prev) =>
        prev.map((obj) => {
          if (obj.id === id) {
            const isWall = ['wall', 'wall_corner', 'wall_t', 'wall_x'].includes(
              obj.type
            );

            const newRotation =
              ((obj.rotation || 0) + (isWall ? 45 : 90)) % 360;

            if (isWall) {
              setDefaultRotate(newRotation);
            }

            return { ...obj, rotation: newRotation };
          }

          return obj;
        })
      );
    };

    const handleDeleteObject = (id: number): void => {
      onMapObjectsChange((prev) => prev.filter((obj) => obj.id !== id));
    };

    const [textModal, setTextModal] = useState<{
      isOpen: boolean;
      objectId: number;
      initialText?: string;
      initialTextStyle?: ITextStyle;
      onSave: (text: string, textStyle: ITextStyle) => void;
      onCancel: () => void;
    } | null>(null);

    const openTextModal = (params: {
      objectId: number;
      initialText?: string;
      initialTextStyle?: ITextStyle;
      onSave: (text: string, textStyle: ITextStyle) => void;
      onCancel: () => void;
    }): void => {
      const object = mapObjects.find((o) => o.id === params.objectId);
      const currentText =
        object?.type === 'text' ? object.text || '' : params.initialText ?? '';
      const currentTextStyle =
        object?.type === 'text'
          ? object.textStyle || defaultTextStyle
          : params.initialTextStyle ?? defaultTextStyle;

      setTextModal({
        isOpen: true,
        objectId: params.objectId,
        initialText: currentText,
        initialTextStyle: currentTextStyle,
        onSave: params.onSave,
        onCancel: params.onCancel,
      });
    };

    const closeTextModal = (): void => setTextModal(null);

    useEffect(() => {
      const layoutRef = document.getElementById('root');

      if (!layoutRef) {
        return;
      }

      layoutRef.addEventListener('mousedown', handleMouseDown);
      layoutRef.addEventListener('mousemove', handleMouseMove);
      layoutRef.addEventListener('mouseup', handleMouseUp);
      layoutRef.addEventListener('click', handleClick);

      return () => {
        layoutRef.removeEventListener('mousedown', handleMouseDown);
        layoutRef.removeEventListener('mousemove', handleMouseMove);
        layoutRef.removeEventListener('mouseup', handleMouseUp);
        layoutRef.removeEventListener('click', handleClick);
      };
    }, [handleMouseDown, handleMouseMove, handleMouseUp, handleClick]);

    const mapObjectVisible = useMemo(
      () => (isEdit ? isEdit : !isMinimal),
      [isEdit, isMinimal]
    );

    const bounds = useMemo(() => {
      if (isMinimal) {
        return findBounds(mapItems.map(({ x, y }) => ({ x, y })));
      } else {
        return findBounds([
          ...mapObjects.map(({ x, y }) => ({ x, y })),
          ...mapItems.map(({ x, y }) => ({ x, y })),
        ]);
      }
    }, [mapObjects, mapItems]);

    useEffect(() => {
      const maxId = Math.max(...mapObjects.map((o: IMapObject) => o.id), 999);

      idCounter.current = maxId + 1;
    }, [mapObjects]);

    return (
      <div
        className={cn(s.container, isEdit && s.container_isEdit)}
        ref={localRef}
      >
        <TextEditModal
          isOpen={textModal?.isOpen}
          initialText={textModal?.initialText}
          initialTextStyle={{
            ...(textModal?.initialTextStyle || defaultTextStyle),
          }}
          onSave={(text, textStyle) => {
            textModal?.onSave(text.trim(), textStyle);
            closeTextModal();
          }}
          onCancel={() => {
            textModal?.onCancel();
            closeTextModal();
          }}
          setDefaultTextStyle={setDefaultTextStyle}
        />
        <DndContext
          sensors={sensors}
          onDragStart={() => {
            suppressMapClick.current = true;
          }}
          onDragCancel={() => {
            suppressMapClick.current = false;
          }}
          onDragEnd={(event) => {
            const { active, over } = event;
            const activeEntity = parseMapDragId(active.id);
            const clearDragClick = (): void => {
              setTimeout(() => {
                suppressMapClick.current = false;
              }, 0);
            };
            if (!over || !isEdit || !activeEntity) {
              clearDragClick();
              return;
            }

            const overId = String(over.id);
            if (activeEntity.kind === 'object') {
              const mapObject = mapObjects.find(
                (obj) => obj.id === activeEntity.id
              );
              if (!mapObject) {
                clearDragClick();
                return;
              }
              if (!overId.includes('-')) {
                clearDragClick();
                return;
              }

              const [newY, newX] = overId.split('-').map(Number);
              if (isNaN(newX) || isNaN(newY)) {
                clearDragClick();
                return;
              }

              const occupiedByHost = mapItems.some(
                (item) => item.x === newX && item.y === newY
              );
              const occupiedByObject = mapObjects.some((obj) => {
                return (
                  obj.id !== activeEntity.id && obj.x === newX && obj.y === newY
                );
              });

              if (occupiedByHost || occupiedByObject) {
                clearDragClick();
                return;
              }

              onMapObjectsChange((prev) =>
                prev.map((obj) => {
                  return obj.id === activeEntity.id
                    ? { ...obj, x: newX, y: newY }
                    : obj;
                })
              );
              clearDragClick();
              return;
            }

            if (mapItems.some((el) => `${el.y}-${el.x}` === overId)) {
              clearDragClick();
              return;
            }

            const [y, x] = overId.split('-').map(Number);
            if (isNaN(x) || isNaN(y)) {
              clearDragClick();
              return;
            }

            const occupiedByObject = mapObjects.some(
              (obj) => obj.x === x && obj.y === y
            );
            if (occupiedByObject) {
              clearDragClick();
              return;
            }

            const hostData = apolloClient.readFragment<IHostInputFragment>({
              id: apolloClient.cache.identify({
                id: activeEntity.id,
                __typename: 'HostOverview',
              }),
              fragment: HOST_INPUT_FR,
            });

            if (!hostData) {
              clearDragClick();
              return;
            }

            setMapItems((prev) => ({
              ...prev,
              [activeEntity.id]: {
                ...prev[activeEntity.id],
                x,
                y,
              },
            }));
            clearDragClick();
          }}
        >
          <div
            className={cn(s.map, isEdit && s.map_hidden)}
            onClick={(e: React.MouseEvent) => {
              if (suppressMapClick.current) {
                suppressMapClick.current = false;
                return;
              }
              if (!isEdit || isSave) return;

              const target = e.target as HTMLElement;
              const droppable = target.closest('[data-droppable-id]');
              if (!droppable) return;

              const cellId = (droppable as HTMLElement).dataset.droppableId;
              if (!cellId) return;

              const [y, x] = cellId.split('-').map(Number);

              const isCellOccupied =
                mapItems.some((item) => item.x === x && item.y === y) ||
                mapObjects.some((obj) => obj.x === x && obj.y === y);

              if (isCellOccupied) return;

              const getToolCategory = (
                tool: MapObjectType | null
              ): 'text' | 'host' | 'object' | 'none' => {
                if (!tool) return 'host';

                if (tool === 'text') return 'text';

                const hostTools = [
                  'choose',
                  'pc',
                  'console',
                  'vr',
                  'racing_sim',
                  'billiard',
                  'board_game',
                ];

                return hostTools.includes(tool) ? 'host' : 'object';
              };

              const toolCategory = getToolCategory(selectedTool);

              if (toolCategory === 'text') {
                const newId = getNextId();

                onMapObjectsChange((prev) => [
                  ...prev,
                  {
                    id: newId,
                    type: 'text' as const,
                    x,
                    y,
                    text: '',
                  },
                ]);

                openTextModal({
                  objectId: newId,
                  initialText: '',
                  initialTextStyle: defaultTextStyle,
                  onSave: (text, textStyle) => {
                    onMapObjectsChange((prev) =>
                      prev.map((obj) =>
                        obj.id === newId ? { ...obj, text, textStyle } : obj
                      )
                    );
                  },
                  onCancel: () => {
                    onMapObjectsChange((prev) =>
                      prev.filter((obj) => obj.id !== newId)
                    );
                  },
                });

                return;
              }

              if (toolCategory === 'object') {
                const newObject: IMapObject = {
                  id: getNextId(),
                  type: selectedTool || 'choose',
                  x,
                  y,
                };

                if (
                  ['wall', 'wall_corner', 'wall_t', 'wall_x'].includes(
                    selectedTool as MapObjectType
                  )
                ) {
                  newObject.rotation = defaultRotate;
                }

                onMapObjectsChange([...mapObjects, newObject]);
                return;
              }

              if (toolCategory === 'host') {
                const hostType: EHostType =
                  selectedTool === 'choose' || !selectedTool
                    ? EHostType.PC
                    : (selectedTool.toUpperCase() as EHostType);

                openHostCreate({ x, y, hostType });
                return;
              }
            }}
            style={{ scale: isEdit ? '1' : String(zoomValue) }}
          >
            {GRID.map((row, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={cn(
                  s.row,
                  bounds &&
                    (bounds.minY > rowIndex || bounds.maxY < rowIndex) &&
                    !isEdit &&
                    s.row_hidden
                )}
              >
                {row.map((cellId, cellIndex) => {
                  const hostItem = mapItems.find(
                    (item) => `${item.y}-${item.x}` === cellId
                  );

                  const mapObjectItem = mapObjects.find(
                    (obj) => `${obj.y}-${obj.x}` === cellId
                  );

                  const childrenId = hostItem?.id || mapObjectItem?.id;
                  const hasConflict = Boolean(hostItem && mapObjectItem);

                  return (
                    <Droppable
                      key={`droppable-${cellId}`}
                      id={cellId}
                      selected={selectedItems.has(String(hostItem?.id))}
                      disabled={
                        isSave || !permissions?.has('host_manage') || !isEdit
                      }
                      childrenId={childrenId}
                      data-droppable-id={cellId}
                      hostId={hostItem?.id}
                      hasConflict={hasConflict}
                      isEdit={isEdit}
                      hidden={
                        bounds && !isEdit
                          ? bounds.minX > cellIndex || bounds.maxX < cellIndex
                          : false
                      }
                    >
                      {hostItem && isEdit && (
                        <DraggableMapHost
                          id={Number(hostItem.id)}
                          host={hostItem}
                          disabled={
                            isSave ||
                            !permissions?.has('host_manage') ||
                            !isEdit
                          }
                          onHostClick={onHostClick}
                          hostModalsState={hostModalsState}
                        />
                      )}
                      {hostItem && !isEdit && (
                        <DraggableItem
                          id={hostItem.id}
                          disabled={
                            isSave ||
                            !permissions?.has('host_manage') ||
                            !isEdit
                          }
                          onHostClick={onHostClick}
                          selectedHostsIds={selectedHostsIds}
                          onContextMenu={onContextMenu}
                          onTouchStart={onTouchStart}
                          onTouchMove={onTouchMove}
                          onTouchEnd={onTouchEnd}
                          onHostDoubleClick={onHostDoubleClick}
                          openBooking={openBooking}
                          canOpenTooltip={canOpenTooltip}
                          setClientUUID={setClientUUID}
                          isSelecting={isSelecting}
                          setSelectedItems={setSelectedItems}
                        />
                      )}

                      {mapObjectVisible && mapObjectItem && (
                        <DraggableMapObject
                          obj={mapObjectItem}
                          isEdit={isEdit}
                          onRotate={handleRotateObject}
                          onDelete={handleDeleteObject}
                          openTextModal={openTextModal}
                          onMapObjectsChange={onMapObjectsChange}
                          hasConflict={hasConflict}
                          isSave={isSave}
                          defaultTextStyle={defaultTextStyle}
                        />
                      )}
                    </Droppable>
                  );
                })}
              </div>
            ))}
            <DraggableOverlay />
          </div>
        </DndContext>

        {isSelecting &&
          (() => {
            const root = document.getElementById('root');

            if (!root) return null;

            return createPortal(
              <div style={selectionStyle} className={s.selected_frame}></div>,
              root
            );
          })()}
      </div>
    );
  }
);
Map.displayName = 'Map';
